"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Reveal = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const app_1 = require("@obvio/app");
const utils_1 = require("@obvio/utils");
const react_1 = require("react");
const Wrap = app_1.styled.div `
  position: relative;
  width: 100%;
  height: 100%;
`;
const Canvas = app_1.styled.canvas `
  height: 100%;
  z-index: 1;
  position: absolute;
  inset: 0;

  @media ${app_1.notDesktop} {
    display: none;
  }
`;
function drawDot(ctx, x, y, size) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, size, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.fill();
}
function drawBaseCanvas(canvas, ctx, color) {
    const parent = canvas.parentElement;
    const { clientWidth: width, clientHeight: height } = parent !== null && parent !== void 0 ? parent : {
        clientHeight: window.innerWidth,
        clientWidth: window.innerHeight,
    };
    canvas.width = width;
    canvas.height = height;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, width, height);
}
function initializeCanvas(canvas, color, size, posX, posY, initDot) {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error("Couldn't initialize canvas");
    }
    drawBaseCanvas(canvas, ctx, color);
    if (initDot) {
        drawDot(ctx, canvas.width * posX, canvas.height * posY, size);
    }
}
function getRelativePosition(canvas, mouse) {
    const { top, left } = canvas === null || canvas === void 0 ? void 0 : canvas.getBoundingClientRect();
    let { x, y } = mouse;
    x -= left;
    y -= top;
    return [x, y];
}
function Reveal({ children, color, size = 250, posX = 0.5, posY = 0.5, }) {
    const isMobile = (0, app_1.useIsMobile)();
    const theme = (0, app_1.useTheme)();
    const revealerColor = color !== null && color !== void 0 ? color : theme.colors.secondary;
    const canvasRef = (0, react_1.useRef)(null);
    const posRef = (0, react_1.useRef)(null);
    const targetRef = (0, react_1.useRef)(null);
    const mouseRef = (0, react_1.useRef)(null);
    const animate = (0, react_1.useCallback)(() => {
        var _a;
        const canvas = canvasRef.current;
        const ctx = (_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getContext('2d');
        if (ctx &&
            canvasRef.current &&
            canvas &&
            posRef.current &&
            targetRef.current) {
            let { x, y } = posRef.current;
            const { x: targetX, y: targetY } = targetRef.current;
            const distX = targetX - x;
            const distY = targetY - y;
            const { left, height, width } = canvas.getBoundingClientRect();
            const xMax = left + width - size;
            const xMin = size;
            const yMin = size;
            const yMax = height - size;
            x = (0, utils_1.minmax)(x + distX * 0.025, xMin, xMax);
            y = (0, utils_1.minmax)(y + distY * 0.025, yMin, yMax);
            posRef.current = { x, y };
            drawBaseCanvas(canvas, ctx, revealerColor);
            drawDot(ctx, x, y, size);
            if (!isMobile) {
                requestAnimationFrame(animate);
            }
        }
    }, [isMobile, revealerColor, size]);
    const draw = (0, react_1.useCallback)(() => {
        var _a;
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        const ctx = (_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getContext('2d');
        if (ctx && canvasRef.current && canvas && mouseRef.current) {
            drawBaseCanvas(canvas, ctx, revealerColor);
            const [x, y] = getRelativePosition(canvas, mouseRef.current);
            const centerX = canvas.width * posX;
            const centerY = canvas.height * posY;
            const diffX = (centerX / 2 - x) / 2;
            const diffY = (centerY / 2 - y) / 4;
            const { height } = canvas.getBoundingClientRect();
            const targetX = (0, utils_1.minmax)(centerX - diffX, 0, window.innerWidth);
            const targetY = (0, utils_1.minmax)(centerY - diffY, 0, height - size);
            targetRef.current = { x: targetX, y: targetY };
        }
    }, [posX, posY, revealerColor, size]);
    const handleMouseMove = (0, react_1.useCallback)((ev) => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }
        mouseRef.current = { x: ev.clientX, y: ev.clientY };
        draw();
    }, [draw]);
    const handelScroll = (0, react_1.useCallback)(() => {
        draw();
    }, [draw]);
    (0, react_1.useEffect)(() => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            initializeCanvas(canvas, revealerColor, size, posX, posY, true);
            const centerX = canvas.width * posX;
            const centerY = canvas.height * posY;
            const center = { x: centerX, y: centerY };
            posRef.current = center;
            targetRef.current = center;
            mouseRef.current = center;
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('scroll', handelScroll);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('scroll', handelScroll);
            };
        }
    }, [
        canvasRef,
        revealerColor,
        handleMouseMove,
        size,
        posX,
        posY,
        handelScroll,
    ]);
    (0, react_1.useEffect)(() => {
        if (!isMobile) {
            animate();
        }
    }, [animate, isMobile]);
    return ((0, jsx_runtime_1.jsxs)(Wrap, { children: [children, (0, jsx_runtime_1.jsx)(Canvas, { ref: canvasRef })] }));
}
exports.Reveal = Reveal;
